import { useState, useEffect } from 'react';
import { fetchAllMappings, fetchFieldSyncLogs } from '../lib';
import { getSystemHealth, testApiConnection } from '../lib/system';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ReloadIcon, CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * A simple component to test our API connections
 */
export function ApiStatus() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [results, setResults] = useState<any>({});
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [apiConnectionStatus, setApiConnectionStatus] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  const testConnections = async () => {
    setStatus('loading');
    setError(null);
    
    try {
      // Get system health information
      const health = await getSystemHealth().catch((e: any) => ({
        status: 'error',
        message: e.message,
        timestamp: new Date().toISOString()
      }));
      setSystemHealth(health);
      
      // Test direct API connection
      const apiConnection = await testApiConnection();
      setApiConnectionStatus(apiConnection);
      
      // Test individual endpoints
      const endpointResults = {
        mappings: await fetchAllMappings().catch((e: any) => ({error: e.message})),
        fieldSyncLogs: await fetchFieldSyncLogs(undefined, undefined, undefined, 25).catch((e: any) => ({ error: e.message })),
      };
      
      setResults(endpointResults);
      setStatus('success');
      console.log('API test results:', { health, apiConnection, endpoints: endpointResults });
    } catch (error: any) {
      setStatus('error');
      setError(error.message || 'Unknown error');
      console.error('API test failed:', error);
    }
  };
  
  useEffect(() => {
    testConnections();
  }, []);

  // Helper to determine if we're getting real data or mock data
  const isMockData = (data: any) => {
    if (!data) return false;
    if (data.mockData === true) return true;
    // Empty arrays often indicate mock data fallbacks
    if (Array.isArray(data) && data.length === 0) return true;
    // Empty field mappings and choice mappings are likely mock data
    if (data.fieldMappings && data.choiceMappings && 
        data.fieldMappings.length === 0 && data.choiceMappings.length === 0) {
      return true;
    }
    return false;
  };
  
  return (
    <Card className="w-full max-w-3xl mx-auto mt-8">
      <CardHeader>
        <CardTitle>API Connection Status</CardTitle>
        <CardDescription>
          Testing connections to the API endpoints using our simplified backend proxy
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {status === 'loading' && (
          <div className="flex items-center justify-center p-2">
            <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
            <span>Testing API connections...</span>
          </div>
        )}
        
        {status === 'error' && (
          <Alert variant="destructive" className="mb-4">
            <AlertTitle>Connection Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {status === 'success' && (
          <div className="space-y-6">
            {/* System Health Check Panel */}
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-medium mb-2">System Status</h3>
              
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2">Backend Status:</span>
                  <Badge variant={systemHealth?.status === 'ok' ? 'success' : 'destructive'}>
                    {systemHealth?.status === 'ok' ? 'Online' : 'Error'}
                  </Badge>
                </div>
                
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2">Environment:</span>
                  <Badge variant="outline">
                    {systemHealth?.environment || 'Unknown'}
                  </Badge>
                </div>
                
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2">API Key:</span>
                  {systemHealth?.apiConfigured ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Configured
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      Missing
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2">API Connection:</span>
                  {apiConnectionStatus?.status === 'success' || systemHealth?.apiConnection?.status === 'connected' ? (
                    <div className="flex items-center">
                      <CheckCircledIcon className="text-green-500 mr-1 h-4 w-4" />
                      <span className="text-sm text-green-700">
                        {isMockData(apiConnectionStatus) ? 'Using Fallback Data' : 'Connected'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <CrossCircledIcon className="text-red-500 mr-1 h-4 w-4" />
                      <span className="text-sm text-red-600">
                        {systemHealth?.apiConnection?.status === 'unconfigured' ? 'Not Configured' : 'Disconnected'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                Last checked: {new Date(systemHealth?.timestamp || Date.now()).toLocaleTimeString()}
              </div>
            </div>
            
            {/* Endpoints Status Tabs */}
            <Tabs defaultValue="mappings">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="mappings">All Mappings</TabsTrigger>
                <TabsTrigger value="sync-logs">Sync Logs</TabsTrigger>
              </TabsList>
              
              <TabsContent value="mappings" className="mt-4">
                <div className="flex items-center mb-2">
                  <h3 className="text-lg font-medium flex-1">Mappings Response</h3>
                  {isMockData(results.mappings) && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Fallback Data
                    </Badge>
                  )}
                </div>
                <pre className="bg-slate-100 dark:bg-slate-800 p-3 rounded text-xs overflow-auto max-h-64">
                  {JSON.stringify(results.mappings, null, 2)}
                </pre>
              </TabsContent>
              
              <TabsContent value="sync-logs" className="mt-4">
                <div className="flex items-center mb-2">
                  <h3 className="text-lg font-medium flex-1">Sync Logs Response</h3>
                  {isMockData(results.fieldSyncLogs) && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Fallback Data
                    </Badge>
                  )}
                </div>
                <pre className="bg-slate-100 dark:bg-slate-800 p-3 rounded text-xs overflow-auto max-h-64">
                  {JSON.stringify(results.fieldSyncLogs, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </CardContent>
      
      <CardFooter>
        <Button 
          onClick={testConnections}
          disabled={status === 'loading'}
        >
          {status === 'loading' && <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />}
          Test API Connections
        </Button>
      </CardFooter>
    </Card>
  );
}
